/**
 * Right-to-Work (RTW) entity DTOs.
 *
 * Mirrors Prisma `RightToWorkCheck` (prisma/schema.prisma:1564) — an
 * IMMUTABLE evidence record of a manual Home Office share-code / document
 * check against an account worker. There is no edit/delete: a correction is
 * a new check (the latest by `checkedAt`, tie-broken by `createdAt`, wins).
 *
 * Web equivalent: `app/api/account-workers/[workerId]/right-to-work/*` +
 * `lib/right-to-work.ts`. The mobile mirror at
 * `/api/mobile/v1/account/workers/:workerId/right-to-work/*` consumes these
 * schemas via `safeParseOrWarn`. Gated by CERT_TRACKING (Pro+), like
 * certifications.
 */
import { z } from "zod";

/** Outcome of a manual right-to-work check (mirrors RightToWorkOutcome enum). */
export const RightToWorkOutcomeSchema = z.enum([
  "ALLOWED",
  "ALLOWED_TIME_LIMITED",
  "NOT_ALLOWED",
  "NOT_PROVEN",
]);

export type RightToWorkOutcome = z.infer<typeof RightToWorkOutcomeSchema>;

/** Derived status of a worker's right to work (latest check). Display-only. */
export const RightToWorkStatusSchema = z.enum([
  "valid",
  "expiring",
  "expired",
  "not_allowed",
  "missing",
]);

export type RightToWorkStatus = z.infer<typeof RightToWorkStatusSchema>;

/** A serialised right-to-work check returned by the mobile API. */
export const RightToWorkCheckSchema = z
  .object({
    id: z.string(),
    outcome: RightToWorkOutcomeSchema,
    shareCode: z.string().nullable(),
    dateOfBirthUsed: z.string().datetime().nullable(),
    checkedAt: z.string().datetime(),
    restrictions: z.string().nullable(),
    expiresAt: z.string().datetime().nullable(),
    photoMatchConfirmed: z.boolean(),
    evidenceFileUrl: z.string().nullable(),
    evidenceFileName: z.string().nullable(),
    notes: z.string().nullable(),
    createdAt: z.string().datetime(),
  });

export type RightToWorkCheck = z.infer<typeof RightToWorkCheckSchema>;

// #2411 server-boundary variant: the mobile app parses the permissive base;
// the route serialises through the Strict form.
export const RightToWorkCheckStrict = RightToWorkCheckSchema.strict();

/**
 * POST body for recording a right-to-work check. Mirrors the web
 * `createSchema` field-for-field, including the two cross-field invariants:
 *
 *  - A time-limited outcome MUST carry a valid expiry, or it would never
 *    enter the cert-expiry cron and read as indefinitely valid.
 *  - An "allowed" outcome MUST confirm the photo matches the person, or we'd
 *    stamp valid RTW evidence without the identity check.
 *
 * `shareCode` format + `evidenceFileUrl` ownership are validated server-side
 * (they need request context).
 */
export const RightToWorkCheckCreateRequest = z
  .object({
    outcome: RightToWorkOutcomeSchema,
    shareCode: z.string().trim().min(1).optional(),
    dateOfBirthUsed: z.string().optional(),
    checkedAt: z.string().optional(),
    restrictions: z.string().optional(),
    expiresAt: z.string().optional(),
    photoMatchConfirmed: z.boolean().optional(),
    evidenceFileUrl: z.string().optional(),
    evidenceFileName: z.string().optional(),
    notes: z.string().optional(),
  })
  .strict()
  .refine(
    (d) =>
      d.outcome !== "ALLOWED_TIME_LIMITED" ||
      (Boolean(d.expiresAt) && !Number.isNaN(Date.parse(d.expiresAt as string))),
    {
      message: "A time-limited right to work requires a valid expiry date.",
      path: ["expiresAt"],
    },
  )
  .refine(
    (d) =>
      (d.outcome !== "ALLOWED" && d.outcome !== "ALLOWED_TIME_LIMITED") ||
      d.photoMatchConfirmed === true,
    {
      message:
        "Confirm the photo matches the person before recording an allowed right to work.",
      path: ["photoMatchConfirmed"],
    },
  );

export type RightToWorkCheckCreateRequest = z.infer<
  typeof RightToWorkCheckCreateRequest
>;
